/**
 * EL RANKING (artefacto «Compartir los menús», 24-09; circuito cerrado el 26-09).
 *
 * «Usuarios», «Menús» y «Nuevos». Solo cuenta lo que la gente comparte con el botón
 * «Compartir» de la comida (César, 24-09: «como guardar una publicación en Instagram»).
 *   - Usuarios: quién más comparte, en el histórico, este mes o esta semana. Arriba, su sitio:
 *     «sin eso, un ranking es una lista de gente que no eres tú».
 *   - Menús: los 30 que más gente usa.
 *   - Nuevos: los compartidos esta semana, del más nuevo al más viejo. Es lo que deja que un
 *     menú recién compartido se vea al lado de los de siempre (César: «que solo diga nuevos»).
 * Pulsar un menú abre su ficha: «Usar en una comida» y «Guardar en favoritas».
 * La primera vez que entra, si no ha contestado, se le pregunta aquí si autoriza compartir.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Star, Utensils, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { plural } from '../lib/labels';
import { num1 } from '../lib/numeros';
import { TarjetaCompartirMenus } from '../components/PreguntaCompartirMenus';

// El id `personas` es el del servidor; cambia lo que se lee (Jesús, 25-09: «Usuarios»).
const PESTANAS = [{ id: 'personas', label: 'Usuarios' }, { id: 'menus', label: 'Menús' }, { id: 'nuevos', label: 'Nuevos' }];
const PERIODOS = [{ id: 'siempre', label: 'Histórico' }, { id: 'mes', label: 'Este mes' }, { id: 'semana', label: 'Esta semana' }];
const EN_EL_PERIODO = { siempre: 'en el histórico', mes: 'este mes', semana: 'esta semana' };
const fechaCorta = (iso) => {
    try { return new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long' }); }
    catch (e) { return ''; }
};
const mayuscula = (t) => `${t.charAt(0).toUpperCase()}${t.slice(1)}`;

const Interruptor = ({ opciones, valor, onCambiar, testId }) => (
    <div className="inline-flex bg-muted rounded-xl p-1 gap-1" role="tablist" data-testid={testId}>
        {opciones.map((o) => (
            <button key={o.id} role="tab" aria-selected={valor === o.id} onClick={() => onCambiar(o.id)}
                data-testid={`${testId}-${o.id}`}
                className={`px-3 py-1.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-colors
                    ${valor === o.id ? 'bg-brand text-white shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}>
                {o.label}
            </button>
        ))}
    </div>
);

const usanLos = (n) => (!n ? 'todavía no los usa nadie' : `${n === 1 ? 'los usa' : 'los usan'} ${plural(n, 'persona')}`);
const usanLa = (n) => (!n ? 'todavía no lo usa nadie' : `${n === 1 ? 'lo usa' : 'lo usan'} ${plural(n, 'persona')}`);

/** La ficha de un menú: qué lleva y los dos botones. */
function FichaDelMenu({ id, onClose, onQuitado }) {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [m, setM] = useState(null);
    const [guardando, setGuardando] = useState(false);

    // `onClose` llega nuevo en cada pintado del ranking: por el ref, para no volver a pedir la ficha.
    const cerrarRef = useRef(onClose);
    cerrarRef.current = onClose;
    useEffect(() => {
        let cancelado = false;
        api.get(`/menus-compartidos/menu/${encodeURIComponent(id)}`)
            .then((r) => { if (!cancelado) setM(r.data); })
            .catch(() => { if (!cancelado) { toast.error('Ese menú ya no está compartido.'); cerrarRef.current(); } });
        return () => { cancelado = true; };
    }, [api, id]);

    const guardarEnFavoritas = async () => {
        setGuardando(true);
        try {
            await api.post('/diets/favorites', {
                name: (m.titulo || 'Menú del ranking').slice(0, 60), ambito: 'comida',
                comida: m.comida_origen || 'C1', alimentos: m.alimentos, macros_snapshot: m.macros,
            });
            api.post(`/menus-compartidos/menu/${encodeURIComponent(m.id)}/usado`, { como: 'favorita' }).catch(() => {});
            toast.success('Guardado en tus favoritas', {
                description: 'Lo tienes en «Favoritas» de cualquier comida.',
            });
            onClose();
        } catch {
            toast.error('No hemos podido guardarlo. Prueba otra vez.');
        } finally {
            setGuardando(false);
        }
    };

    const dejarDeCompartir = async () => {
        setGuardando(true);
        try {
            await api.delete(`/menus-compartidos/${encodeURIComponent(m.id)}`);
            toast.success('Ya no lo compartes', { description: 'Ha salido del ranking.' });
            onQuitado?.();
        } catch {
            toast.error('No hemos podido quitarlo. Prueba otra vez.');
            setGuardando(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
            role="dialog" aria-modal="true" aria-labelledby="ficha-menu-titulo"
            onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="ficha-menu">
            <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl bg-card border border-border shadow-2xl p-5">
                <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0">
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-brand mb-1">
                            {m ? `De ${m.nombre || 'ti'}${m.soy_yo ? ' · tuyo' : ''}` : 'Menú'}
                        </p>
                        <h2 id="ficha-menu-titulo" className="font-heading text-xl font-bold text-foreground leading-tight">
                            {m ? m.titulo : 'Cargando…'}
                        </h2>
                    </div>
                    <button type="button" onClick={onClose} aria-label="Cerrar"
                        className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted flex-shrink-0">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {m && (
                    <>
                        <p className="text-sm text-muted-foreground mb-3">
                            {mayuscula(usanLa(m.usan))}{m.dia ? ` · compartido el ${fechaCorta(m.dia)}` : ''}
                        </p>
                        <ul className="divide-y divide-border rounded-xl border border-border mb-3" data-testid="ficha-menu-alimentos">
                            {(m.alimentos || []).map((a, i) => (
                                <li key={`${a.alimento_id}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                                    <span className="text-foreground min-w-0">{a.nombre}</span>
                                    <span className="text-muted-foreground tabular-nums whitespace-nowrap">{Math.round(Number(a.cantidad_g) || 0)}&nbsp;g</span>
                                </li>
                            ))}
                        </ul>
                        {m.macros && (
                            <p className="text-xs text-muted-foreground mb-4 tabular-nums">
                                Tal como lo comió: {num1(m.macros.P)}&nbsp;g de proteína · {num1(m.macros.H)}&nbsp;g de hidratos · {num1(m.macros.G)}&nbsp;g de grasa.
                                {' '}Al usarlo se cuadra a tu comida.
                            </p>
                        )}
                        <div className="grid gap-2">
                            <button type="button" onClick={() => navigate(`/dashboard/nutrition?menu=${encodeURIComponent(m.id)}`)}
                                data-testid="ficha-menu-usar"
                                className="w-full py-3 rounded-xl bg-brand text-white font-bold text-sm hover:opacity-90 flex items-center justify-center gap-2">
                                <Utensils className="w-4 h-4" /> Usar en una comida
                            </button>
                            <button type="button" onClick={guardarEnFavoritas} disabled={guardando}
                                data-testid="ficha-menu-favorita"
                                className="w-full py-3 rounded-xl border border-border text-foreground font-bold text-sm hover:bg-muted disabled:opacity-60 flex items-center justify-center gap-2">
                                <Star className="w-4 h-4" /> Guardar en favoritas
                            </button>
                            {m.soy_yo && (
                                <button type="button" onClick={dejarDeCompartir} disabled={guardando}
                                    data-testid="ficha-menu-quitar"
                                    className="w-full py-2 text-sm font-semibold text-muted-foreground hover:text-red-500 disabled:opacity-60">
                                    Dejar de compartir este menú
                                </button>
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

export default function RankingPage() {
    const { api, refreshProfile } = useAuth();
    const [tab, setTab] = useState('personas');
    const [periodo, setPeriodo] = useState('mes');   // de entrada, el mes: el que deja ganar a alguien nuevo
    const [datos, setDatos] = useState(null);
    const [recarga, setRecarga] = useState(0);
    const [guardando, setGuardando] = useState(false);
    const [abierto, setAbierto] = useState(null);

    useEffect(() => {
        let cancelado = false;
        setDatos(null);
        api.get('/menus-compartidos/ranking', { params: { tab, periodo } })
            .then((r) => { if (!cancelado) setDatos(r.data); })
            .catch(() => { if (!cancelado) setDatos({ lista: [], error: true }); });
        return () => { cancelado = true; };
    }, [api, tab, periodo, recarga]);

    const contestar = async (valor) => {
        setGuardando(true);
        try {
            await api.put('/menus-compartidos/autorizo', { compartir: valor });
            await refreshProfile();
            setRecarga((n) => n + 1);
        } catch {
            toast.error('No se pudo guardar. Prueba otra vez.');
        } finally {
            setGuardando(false);
        }
    };

    const delPeriodo = periodo !== 'siempre';
    const enElPeriodo = EN_EL_PERIODO[periodo] || 'este mes';
    const mio = datos?.mio;
    // La pregunta, aquí, la primera vez (26-09). Actuando como un cliente no: la contesta él.
    // YA NO SE PREGUNTA (Jesús, 27-09 23:41: «se comparte en todas las comidas por defecto»):
    // comparte todo el que no lo haya apagado en Mi perfil.
    const toca = false;

    return (
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-2xl mx-auto animate-fade-in" data-testid="ranking-page">
            <h1 className="font-heading text-3xl md:text-4xl font-bold uppercase text-foreground leading-none mb-2">
                Ranking
            </h1>
            <p className="text-sm text-muted-foreground mb-5">
                {/* Sin el botón «Compartir», que ya no existe (Jesús, 27-09 23:41: «eso no lo
                    pongáis así»): las comidas de un día cuadrado se comparten solas. */}
                Los menús que comparte la gente. Los tuyos se comparten solos cuando cuadras el día.
            </p>

            {toca && (
                <div className="flex justify-center mb-6" data-testid="ranking-pregunta">
                    <TarjetaCompartirMenus eyebrow="El ranking" guardando={guardando} onContestar={contestar}
                        className="shadow-none max-w-none" />
                </div>
            )}

            <div className="flex flex-wrap items-center gap-2 mb-5">
                <Interruptor opciones={PESTANAS} valor={tab} onCambiar={setTab} testId="ranking-tab" />
                {/* «Nuevos» no va por periodo: son los de esta semana. */}
                {tab !== 'nuevos' && (
                    <Interruptor opciones={PERIODOS} valor={periodo} onCambiar={setPeriodo} testId="ranking-periodo" />
                )}
            </div>

            {/* SU SITIO, ARRIBA DEL TODO. Si dijo que no, se le dice por qué no está y se le
                deja encenderlo aquí mismo. */}
            {datos && !toca && tab === 'personas' && (
                datos.autorizo === false ? (
                    <div className="surface p-4 mb-5" data-testid="ranking-no-comparto">
                        <p className="text-sm font-semibold text-foreground mb-1">No estás en el ranking</p>
                        <p className="text-sm text-muted-foreground mb-3">
                            Para entrar tienes que autorizar compartir tus menús. Luego se comparten solas las comidas de los días que cuadres.
                        </p>
                        <button type="button" onClick={() => contestar(true)} disabled={guardando}
                            data-testid="ranking-activar"
                            className="px-4 py-2 rounded-xl bg-brand text-white text-sm font-bold hover:opacity-90 disabled:opacity-60">
                            Autorizo compartir
                        </button>
                    </div>
                ) : datos.autorizo !== false ? (
                    <div className="rounded-xl border border-brand/40 bg-brand/[0.06] p-4 mb-5 flex items-center gap-4"
                        data-testid="ranking-mio">
                        <span className="font-heading text-4xl font-bold text-brand leading-none tabular-nums min-w-[2.5rem] text-center">
                            {mio ? mio.puesto : '–'}
                        </span>
                        <div className="min-w-0">
                            <p className="text-base font-bold text-foreground">
                                {mio ? `Vas el ${mio.puesto}.º ${enElPeriodo}`
                                    : delPeriodo ? `${mayuscula(enElPeriodo)} todavía no has compartido ningún menú` : 'Todavía no has compartido ningún menú'}
                            </p>
                            <p className="text-sm text-muted-foreground">
                                {mio ? `${plural(mio.menus, 'menú compartido', 'menús compartidos')} · ${usanLos(mio.usan)}`
                                    : 'Cuadra un día en Nutrición y sus comidas se comparten solas.'}
                            </p>
                        </div>
                    </div>
                ) : null
            )}

            {!datos ? (
                <p className="text-sm text-muted-foreground py-10 text-center">Cargando el ranking…</p>
            ) : datos.error ? (
                <p className="text-sm text-muted-foreground py-10 text-center">No se pudo cargar el ranking. Prueba otra vez en un rato.</p>
            ) : datos.lista.length === 0 ? (
                <p className="text-sm text-muted-foreground py-10 text-center" data-testid="ranking-vacio">
                    {tab === 'nuevos' ? 'Esta semana todavía no ha compartido nadie. El primero que comparta, sale aquí.'
                        : delPeriodo ? `${mayuscula(enElPeriodo)} todavía no hay nadie. El primero que comparta, gana.` : 'Todavía no hay nadie compartiendo sus menús.'}
                </p>
            ) : (
                <ol className="divide-y divide-border surface" data-testid="ranking-lista">
                    {datos.lista.map((f) => {
                        const esMenu = tab !== 'personas';
                        const dentro = (
                            <>
                                <span className={`font-heading text-xl font-bold tabular-nums w-8 text-center flex-shrink-0
                                    ${f.puesto <= 3 ? 'text-brand' : 'text-muted-foreground'}`}>{f.puesto}</span>
                                <div className="min-w-0">
                                    {esMenu ? (
                                        <>
                                            <p className="font-semibold text-foreground leading-snug">{f.titulo}</p>
                                            <p className="text-sm text-muted-foreground">
                                                De {f.nombre}{f.soy_yo ? ' · tuyo' : ''} · {tab === 'nuevos' && f.desde
                                                    ? `el ${fechaCorta(f.desde)}` : usanLa(f.usan)}
                                            </p>
                                        </>
                                    ) : (
                                        <>
                                            <p className="font-semibold text-foreground">{f.nombre}{f.soy_yo ? ' · tú' : ''}</p>
                                            <p className="text-sm text-muted-foreground">
                                                {plural(f.menus, 'menú', 'menús')} · {usanLos(f.usan)}
                                            </p>
                                        </>
                                    )}
                                </div>
                            </>
                        );
                        return (
                            <li key={`${f.puesto}-${f.id || f.nombre}`} data-testid={`ranking-fila-${f.puesto}`}
                                className={f.soy_yo ? 'bg-brand/[0.06]' : ''}>
                                {esMenu ? (
                                    <button type="button" onClick={() => setAbierto(f.id)}
                                        className="w-full flex items-center gap-4 px-4 py-3 text-left hover:bg-muted/60 transition-colors">
                                        {dentro}
                                    </button>
                                ) : (
                                    <div className="flex items-center gap-4 px-4 py-3">{dentro}</div>
                                )}
                            </li>
                        );
                    })}
                </ol>
            )}

            {abierto && <FichaDelMenu id={abierto} onClose={() => setAbierto(null)}
                onQuitado={() => { setAbierto(null); setRecarga((n) => n + 1); }} />}
        </div>
    );
}
